import { Action } from '@ngrx/store';
import { Observable, ObservableInput } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AsyncAction } from '../models';
import { handleEffectError } from './error.helper';

/**
 * Default implementation of async action effect.
 *
 * Note: when the source `apiCall` completes WITHOUT emitting (e.g. cancelled by
 * `takeUntil(abort$)` from {@link HttpAbortOnLogoutInterceptor}, by parent
 * `switchMap` resubscription, by an idle-timeout teardown, or by
 * `isSameEncounterFilter` dropping a stale response), this effect intentionally
 * dispatches NEITHER `succeededAction` NOR `failedAction` — the consuming slice
 * stays in whatever state the parent action left it in. An attempt to unstick
 * `inProgress` here with a global
 * `defaultIfEmpty(failedAction(ASYNC_ACTION_ABORTED_CODE))` fallback regressed
 * Patient Overview, Prescriptions and cold-boot on the web mirror — multiple
 * effects elsewhere depend on a silent-complete being a no-op. The cleaner fix
 * for the "stuck save spinner" report lives in
 * {@link HttpAbortOnLogoutInterceptor}, which now only aborts on `logOut`, so
 * legitimate in-flight saves are no longer silent-cancelled by lock-screen
 * RFID-unlock or by `AuthFlow.verify` flows.
 * @param apiCall apiCall
 * @param asyncAction asyncAction
 * @param expectedErrorCodes (do not show unexpected error alert for these codes)
 * @param ignoreUnexpectedErrors (do not show unexpected error alert at all)
 */
export const makeDefaultAsyncActionEffect = <T>(
    apiCall: Observable<T>,
    asyncAction: AsyncAction<any, T>,
    expectedErrorCodes?: string[],
    ignoreUnexpectedErrors = false,
): ObservableInput<Action> => {
    return apiCall.pipe(
        switchMap((result: T) => {
            return [
                asyncAction.succeededAction({
                    payload: result,
                }),
            ];
        }),
        handleEffectError(asyncAction.failedAction, expectedErrorCodes, ignoreUnexpectedErrors),
    );
};
